import { useState } from 'react'
import { router } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PublishIcon from '@mui/icons-material/Publish'
import InventoryIcon from '@mui/icons-material/Inventory'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Markdown from 'react-markdown'
import type { Release } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  app_entry: { id: number; name: string; slug: string }
  releases: Release[]
}

export default function ReleasesIndex({ vendor, app_entry, releases }: Props) {
  const [publishDialogRelease, setPublishDialogRelease] = useState<Release | null>(null)
  const [deleteDialogRelease, setDeleteDialogRelease] = useState<Release | null>(null)

  const handlePublish = () => {
    if (!publishDialogRelease) return
    router.patch(
      `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${publishDialogRelease.id}/publish`,
    )
    setPublishDialogRelease(null)
  }

  const handleDelete = () => {
    if (!deleteDialogRelease) return
    router.delete(
      `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${deleteDialogRelease.id}`,
    )
    setDeleteDialogRelease(null)
  }

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton
          onClick={() => router.visit(`/vendors/${vendor.slug}`)}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            Releases — {app_entry.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {vendor.name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            router.visit(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/new`)
          }
        >
          New Release
        </Button>
      </Box>

      {releases.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No releases yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first release to start versioning this entry.
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              router.visit(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/new`)
            }
          >
            Create First Release
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Changelog</TableCell>
                <TableCell>Packages</TableCell>
                <TableCell>Published</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {releases.map((release) => (
                <TableRow key={release.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        v{release.version}
                      </Typography>
                      {release.latest && (
                        <Chip label="Latest" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={release.status === 'published' ? 'Published' : 'Draft'}
                      size="small"
                      color={release.status === 'published' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    {release.changelog ? (
                      <Box sx={{ '& p': { m: 0 }, '& ul': { m: 0, pl: 2 }, fontSize: '0.875rem' }}>
                        <Markdown>{release.changelog}</Markdown>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No changelog
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<InventoryIcon />}
                      onClick={() =>
                        router.visit(
                          `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${release.id}/deployment_packages`,
                        )
                      }
                    >
                      {release.packages_count ?? 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {release.published_at
                      ? new Date(release.published_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {release.editable && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() =>
                            router.visit(
                              `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${release.id}/edit`,
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setPublishDialogRelease(release)}
                        >
                          <PublishIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialogRelease(release)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!publishDialogRelease} onClose={() => setPublishDialogRelease(null)}>
        <DialogTitle>Publish Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to publish version{' '}
            <strong>v{publishDialogRelease?.version}</strong>? This action cannot be undone —
            published releases cannot be edited or deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogRelease(null)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="primary">
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteDialogRelease} onClose={() => setDeleteDialogRelease(null)}>
        <DialogTitle>Delete Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete draft version{' '}
            <strong>v{deleteDialogRelease?.version}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogRelease(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  )
}
